import { captureException } from "./utils/sentry";

/**
 * Enhanced Web Vitals reporting with performance issue detection
 * Identifies slow metrics and sends actionable alerts to Sentry
 */

const thresholds = {
  LCP: { good: 2500, poor: 4000 },
  FID: { good: 100, poor: 300 },
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  TTFB: { good: 600, poor: 1500 },
};

const getRating = (metric, value) => {
  const threshold = thresholds[metric];
  if (!threshold) return 'unknown';
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
};

const diagnosePerformanceIssue = (name, value, entries) => {
  const issues = [];

  if (name === 'LCP') {
    // Largest Contentful Paint - slow page load
    if (value > 4000) {
      const lcpElement = entries?.[0]?.element;
      issues.push({
        issue: 'Slow LCP',
        value: `${Math.round(value)}ms`,
        suggestion: 'Optimize largest image or text block',
        element: lcpElement?.tagName || 'unknown',
        elementSrc: lcpElement?.src || lcpElement?.textContent?.substring(0, 50) || 'unknown'
      });
    }
  }

  if (name === 'FID') {
    // First Input Delay - slow interactivity
    if (value > 300) {
      issues.push({
        issue: 'Slow FID',
        value: `${Math.round(value)}ms`,
        suggestion: 'Reduce JavaScript execution time, use code splitting'
      });
    }
  }

  if (name === 'CLS') {
    // Cumulative Layout Shift - layout instability
    if (value > 0.25) {
      const shifts = entries?.filter(e => e.hadRecentInput === false) || [];
      issues.push({
        issue: 'High CLS',
        value: value.toFixed(3),
        suggestion: 'Add width/height to images, reserve space for ads',
        shiftsCount: shifts.length,
        topShift: shifts[0]?.sources?.[0]?.node?.outerHTML?.substring(0, 100) || 'unknown'
      });
    }
  }

  if (name === 'TTFB') {
    // Time to First Byte - slow server response
    if (value > 1500) {
      issues.push({
        issue: 'Slow TTFB',
        value: `${Math.round(value)}ms`,
        suggestion: 'Optimize API response time, enable CDN caching'
      });
    }
  }

  return issues;
};

const reportWebVitals = (onPerfEntry) => {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    import("web-vitals").then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {

      const handleMetric = (metric) => {
        const { name, value, rating, entries } = metric;
        const customRating = getRating(name, value);

        // Send to custom callback (Google Analytics)
        onPerfEntry({ ...metric, rating: customRating });

        // Diagnose performance issues
        const issues = diagnosePerformanceIssue(name, value, entries);

        // Log to console in development
        if (process.env.NODE_ENV === 'development') {
          console.group(`[Web Vitals] ${name}`);
          console.log('Value:', name === 'CLS' ? value.toFixed(3) : `${Math.round(value)}ms`);
          console.log('Rating:', customRating);
          if (issues.length > 0) {
            console.warn('Performance Issues:', issues);
          }
          console.groupEnd();
        }

        // Alert on poor performance in production
        if (customRating === 'poor' && process.env.REACT_APP_IS_PROD === 'true') {
          captureException(`Poor ${name} detected: ${Math.round(value)}${name === 'CLS' ? '' : 'ms'}`, {
            contexts: {
              webVital: {
                name,
                value: Math.round(value),
                rating: customRating,
                url: window.location.href,
                issues: issues.length > 0 ? issues : undefined
              }
            },
            level: 'warning',
            tags: {
              metric: name,
              rating: customRating
            }
          });
        }
      };

      getCLS(handleMetric);
      getFID(handleMetric);
      getFCP(handleMetric);
      getLCP(handleMetric);
      getTTFB(handleMetric);
    });
  }
};

export default reportWebVitals;
